import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import '../../css/accounts/Sales.css';

const Sales = () => {
  const [salesData, setSalesData] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');

  // 🎯 Pagination States (15 items per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Fetch Express Orders Data from Firebase Firestore
  useEffect(() => {
    const q = query(collection(db, 'expressOrder'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs.map((doc) => {
        const data = doc.data();
        const payment = data.payment || {};

        return {
          id: doc.id,
          // Root Fields
          createdAt: data.createdAt ? data.createdAt.toDate() : null,
          displayOrderId: data.displayOrderId || data.autoGeneratedId || doc.id,
          userName: data.userName || data.customerName || 'N/A',
          userPhone: data.userPhone || data.customerPhone || 'N/A',
          orderStatus: data.orderStatus || 'Pending',
          serviceQuality: data.serviceQuality || data.serviceCategory || 'Standard Express',
          
          // Nested 'payment' Object Fields
          deliveryFee: payment.deliveryFee !== undefined ? Number(payment.deliveryFee) : 0,
          tax: payment.tax !== undefined ? Number(payment.tax) : 0,
          subtotal: payment.subtotal !== undefined ? Number(payment.subtotal) : 0,
          totalPayable: payment.totalPayable !== undefined ? Number(payment.totalPayable) : 0,
          paymentStatus: payment.status || 'Unpaid',
        };
      });

      setSalesData(records);
      setFilteredSales(records);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching express orders sales data: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter Logic
  useEffect(() => {
    let result = salesData;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.userName.toLowerCase().includes(term) ||
        item.userPhone.includes(term) ||
        item.displayOrderId.toLowerCase().includes(term)
      );
    }

    if (filterDate) {
      result = result.filter(item => {
        if (!item.createdAt) return false;
        const itemDate = item.createdAt.toISOString().split('T')[0];
        return itemDate === filterDate;
      });
    }

    if (statusFilter !== 'All') {
      result = result.filter(item => item.orderStatus.toLowerCase() === statusFilter.toLowerCase());
    }

    if (paymentStatusFilter !== 'All') {
      result = result.filter(item => item.paymentStatus.toLowerCase() === paymentStatusFilter.toLowerCase());
    }

    setFilteredSales(result);
    setCurrentPage(1); // Reset to page 1 whenever filters change
  }, [searchTerm, filterDate, statusFilter, paymentStatusFilter, salesData]);

  // Aggregate Totals Calculation
  const totalRevenue = filteredSales.reduce((sum, item) => sum + item.totalPayable, 0);
  const totalServiceAmt = filteredSales.reduce((sum, item) => sum + item.subtotal, 0);
  const totalVAT = filteredSales.reduce((sum, item) => sum + item.tax, 0);
  const totalDelivery = filteredSales.reduce((sum, item) => sum + item.deliveryFee, 0);

  // 🎯 Pagination Calculations
  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayedSales = filteredSales.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="sales-container">
      <div className="sales-card">
        
        {/* Header Section */}
        <div className="sales-header">
          <div>
            <h2 className="sales-title">Sales Ledger & Revenue Report</h2>
            <p className="sales-subtitle">Financial breakdown of express laundry orders</p>
          </div>
          <div className="sales-badge">
            Total Orders: <strong>{filteredSales.length}</strong>
          </div>
        </div>

        {/* Financial Summary KPI Cards */}
        <div className="kpi-grid">
          <div className="kpi-card total-revenue">
            <span className="kpi-label">Total Revenue</span>
            <span className="kpi-value">AED {totalRevenue.toFixed(2)}</span>
          </div>
          <div className="kpi-card service-amount">
            <span className="kpi-label">Total Service Amt</span>
            <span className="kpi-value">AED {totalServiceAmt.toFixed(2)}</span>
          </div>
          <div className="kpi-card vat-amount">
            <span className="kpi-label">Total VAT (Tax)</span>
            <span className="kpi-value">AED {totalVAT.toFixed(2)}</span>
          </div>
          <div className="kpi-card delivery-amount">
            <span className="kpi-label">Total Delivery Cost</span>
            <span className="kpi-value">AED {totalDelivery.toFixed(2)}</span>
          </div>
        </div>

        {/* Filters Toolbar */}
        <div className="sales-filter-bar">
          <div className="filter-group">
            <input 
              type="text" 
              placeholder="Search Invoice, Customer, Phone..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sales-input"
            />
            <input 
              type="date" 
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="sales-input date-input"
            />
          </div>

          <div className="filter-group">
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="sales-select"
            >
              <option value="All">All Order Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Assigned">Assigned</option>
              <option value="Accepted">Accepted</option>
              <option value="Picked Up">Picked Up</option>
              <option value="In-Transit">In-Transit</option>
              <option value="Delivered">Delivered</option>
            </select>

            <select 
              value={paymentStatusFilter} 
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              className="sales-select"
            >
              <option value="All">All Payment Statuses</option>
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid / Pending</option>
              <option value="COD">COD</option>
            </select>
          </div>
        </div>

        {/* Sales Table */}
        <div className="sales-table-wrapper">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Customer Ph</th>
                <th>Order Status</th>
                <th>Service Category</th>
                <th>Service Amount</th>
                <th>VAT</th>
                <th>Delivery Cost</th>
                <th>Total Amount</th>
                <th style={{ textAlign: 'center' }}>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="status-cell">Loading sales data...</td>
                </tr>
              ) : currentDisplayedSales.length === 0 ? (
                <tr>
                  <td colSpan="11" className="status-cell">No sales records found.</td>
                </tr>
              ) : (
                currentDisplayedSales.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.createdAt 
                        ? item.createdAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'N/A'
                      }
                    </td>
                    <td className="invoice-id">{item.displayOrderId}</td>
                    <td className="customer-name">{item.userName}</td>
                    <td>{item.userPhone}</td>
                    <td>
                      <span className={`status-pill ${item.orderStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                        {item.orderStatus}
                      </span>
                    </td>
                    <td>
                      <span className="category-chip">{item.serviceQuality}</span>
                    </td>
                    <td className="currency-cell">AED {item.subtotal.toFixed(2)}</td>
                    <td className="currency-cell">AED {item.tax.toFixed(2)}</td>
                    <td className="currency-cell">AED {item.deliveryFee.toFixed(2)}</td>
                    <td className="currency-cell total-highlight">
                      AED {item.totalPayable.toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`payment-pill ${item.paymentStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                        {item.paymentStatus}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 🎯 Pagination Controls */}
        {!loading && filteredSales.length > 0 && (
          <div className="pagination-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredSales.length)}</strong> of <strong>{filteredSales.length}</strong> records
            </div>

            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: currentPage === 1 ? '#f1f5f9' : '#ffffff',
                  color: currentPage === 1 ? '#94a3b8' : '#334155',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prevPage = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prevPage && page - prevPage > 1 && <span style={{ color: '#94a3b8' }}>...</span>}
                      <button
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: currentPage === page ? '1px solid #2563eb' : '1px solid #cbd5e1',
                          backgroundColor: currentPage === page ? '#2563eb' : '#ffffff',
                          color: currentPage === page ? '#ffffff' : '#334155',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: currentPage === page ? '700' : '500'
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: currentPage === totalPages ? '#f1f5f9' : '#ffffff',
                  color: currentPage === totalPages ? '#94a3b8' : '#334155',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Sales;